/*
  ════════════════════════════════════════════════════════════════════════════
  EVENT TIMES

  Events are stored as instants (UTC) because one starts at 09:00 and ends at
  14:30 — unlike a pickup code, which is per-day and stores a WAT date string.

  THE TRAP THIS FILE EXISTS TO AVOID

  `<input type="datetime-local">` reads and writes LOCAL wall-clock time with no
  zone, so the two conversions are not symmetrical with `toISOString()`:

    new Date('2026-10-21T09:00')          → 09:00 local   (correct)
    new Date(iso).toISOString().slice(0,16) → the UTC wall clock, NOT local

  Using the second as the input's value is the common bug: a 09:00 event shows
  as 09:00 in Accra (UTC+0 in winter, so it looks fine) and as 08:00 or 10:00
  anywhere else — and then saving writes that shifted time back. Both directions
  are done explicitly here.
  ════════════════════════════════════════════════════════════════════════════
*/

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant → the `YYYY-MM-DDTHH:mm` a datetime-local input expects, in local time. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What a datetime-local input gave us → an ISO instant. Empty input → null. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value); // no zone suffix, so this is parsed as local time
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/*
  Today, as the API means it.

  Pickup codes, the register and the dismissal queue are all keyed on a WAT
  date STRING, not an instant, and the server decides it with the same
  arithmetic (`getTodayWAT` in apps/api/src/lib/crypto.ts). Three screens had
  grown their own copy of this; one of them differing by an hour would mean a
  register written to the wrong day, so there is one copy now.
*/
export function todayWAT(): string {
  return new Date(Date.now() + 3_600_000).toISOString().slice(0, 10);
}

const dayFmt = new Intl.DateTimeFormat('en-GH', { weekday: 'short', day: 'numeric', month: 'short' });
const dayYearFmt = new Intl.DateTimeFormat('en-GH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-GH', { hour: '2-digit', minute: '2-digit' });

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * One readable line for when an event runs:
 *   "Tue 21 Oct, 09:00 – 15:00"   same day, timed
 *   "Tue 21 Oct"                  same day, all day
 *   "Tue 21 Oct – Thu 23 Oct"     spanning days
 */
export function formatEventWhen(startAt: string, endAt: string, allDay: boolean): string {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const thisYear = start.getFullYear() === new Date().getFullYear();
  const fmtDay = thisYear ? dayFmt : dayYearFmt;

  if (!sameDay(start, end)) return `${fmtDay.format(start)} – ${fmtDay.format(end)}`;
  if (allDay) return fmtDay.format(start);
  return `${fmtDay.format(start)}, ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
}

/** "in 3 days", "tomorrow", "today", "2 days ago" — relative to now. */
export function relativeDay(iso: string): string {
  const target = new Date(iso);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(target) - startOf(new Date())) / 86_400_000);

  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}

export const EVENT_CATEGORY_LABELS: Record<string, string> = {
  sports: 'Sports',
  academic: 'Academic',
  meeting: 'Meeting',
  holiday: 'Holiday',
  exam: 'Exam',
  other: 'Other',
};
